import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';
import type { RecommendationItem } from 'voyager';
import { BasketDrawerTemplate } from 'voyager';
import type { BasketDrawerView } from 'voyager';
import { Routes } from '@common/constants';
import { useTranslations } from '@lang';
import { entitySelectors } from '@store/entity/selectors';
import { modalErrorSelectors } from '@store/modalError/selectors';
import { ReduxPageType } from '@common-types/ReduxPage';
import { useLabels } from '@features/Menu/useLabels';
import { useRemoveBasketItem } from '@features/Basket/hooks/useRemoveBasketItem';
import { getItineraryItems } from '@utils/getItineraryView';
import { getLanguageCodeFromLocale } from '@utils/getLanguageCodeFromLocale';
import { useBasketHoldNotice } from '@common/components/Notifications';
import {
  calculateTotalNumberOfItemsInBasket,
  getModuleLabel,
  mapModules,
} from '../BasketDrawer.helpers';
import { ModuleType } from '../BasketListing.types';
import { useModulesToRecommend } from '../hooks/useModulesToRecommend';
import { useStartCheckout } from '../hooks/useStartCheckout';
import { useHideArrivalTimes } from '@features/spa/hooks/useHideArrivalTimes';
import { analytics } from '@features/analytics/utilities/analytics';
import { hasOnlyNoValueTableBookings } from '@utils/basket/noValueTableBooking';
import { toBasketCardItems } from './adapters/toBasketCardItem';
import { toItineraryGuests } from './adapters/toItineraryGuests';

type BasketDrawerV2ConnectedProps = {
  open: boolean;
  onClose: () => void;
  propertyModules: string[];
};

const RECOMMENDATIONS_LIST_NAME = 'basket_want_more';

export const BasketDrawerV2Connected: FC<BasketDrawerV2ConnectedProps> = ({
  open,
  onClose,
  propertyModules,
}) => {
  const { t, locale } = useTranslations();
  const language = getLanguageCodeFromLocale(locale);
  const labels = useLabels();
  const router = useRouter();
  const unacknowledgedError = useSelector(modalErrorSelectors.getModalError);
  const spaPage = useSelector(entitySelectors.getSpaPage) as ReduxPageType;
  const tablesPage = useSelector(entitySelectors.getTablesPage) as ReduxPageType;
  const roomsPage = useSelector((state: any) =>
    entitySelectors.getPage(state, 'rooms')
  ) as ReduxPageType;
  const golfPage = useSelector((state: any) =>
    entitySelectors.getPage(state, 'golf')
  ) as ReduxPageType;
  const [view, setView] = useState<BasketDrawerView>('itemised');
  const { startCheckout } = useStartCheckout();
  const {
    removeItem,
    removeExtra,
    removingItemId,
    removingExtra,
    isUpdating,
    displayBasketDetails,
  } = useRemoveBasketItem(language);
  const basketDetails = displayBasketDetails as any;
  const canRemoveExtras = useFeatureIsOn('feature.remove-extras-from-basket');
  const holdNotice = useBasketHoldNotice(open ? basketDetails?.items : undefined);

  const guestItineraries = getItineraryItems(basketDetails?.items ?? [], t, labels);

  const hideArrivalTimes = useHideArrivalTimes();

  const items = useMemo(
    () =>
      toBasketCardItems(basketDetails?.items ?? [], {
        t,
        currencyCode: basketDetails?.total?.currencyCode ?? '',
        moduleImages: {
          rooms: roomsPage?.heroBackgroundImage?.url,
          spa: spaPage?.heroBackgroundImage?.url,
          tables: tablesPage?.heroBackgroundImage?.url,
          golf: golfPage?.heroBackgroundImage?.url,
        },
        hideArrivalTimes,
      }),
    [
      basketDetails?.items,
      basketDetails?.total?.currencyCode,
      t,
      roomsPage?.heroBackgroundImage?.url,
      spaPage?.heroBackgroundImage?.url,
      tablesPage?.heroBackgroundImage?.url,
      golfPage?.heroBackgroundImage?.url,
      hideArrivalTimes,
    ]
  );

  const guests = useMemo(() => toItineraryGuests(guestItineraries), [guestItineraries]);

  const recommendedModules = useModulesToRecommend({
    modules: propertyModules as ModuleType[],
    basketItems: basketDetails?.items,
  });

  const recommendedEntries = useMemo(
    () =>
      recommendedModules.flatMap((module) => {
        const [page] = mapModules([module], { spaPage, tablesPage, roomsPage, golfPage });
        const name = getModuleLabel(module, labels, t);
        if (!page || !name) return [];
        return [
          {
            module,
            item: {
              id: page.route,
              name,
              imageUrl: page.heroBackgroundImage?.url,
            } satisfies RecommendationItem,
          },
        ];
      }),
    [recommendedModules, spaPage, tablesPage, roomsPage, golfPage, labels, t]
  );
  const recommendations = useMemo(
    () => recommendedEntries.map(({ item }) => item),
    [recommendedEntries]
  );

  const reportedThisOpening = useRef(false);

  useEffect(() => {
    if (!open) reportedThisOpening.current = false;
  }, [open]);

  const reportRecommendationsSeen = () => {
    if (reportedThisOpening.current || recommendedEntries.length === 0) return;
    reportedThisOpening.current = true;
    analytics.track({
      event: 'view_item_list',
      params: {
        item_list_name: RECOMMENDATIONS_LIST_NAME,
        items: recommendedEntries.map(({ module }, index) => ({ item_name: module, index })),
      },
    });
  };

  const selectRecommendation = (id: string) => {
    const index = recommendedEntries.findIndex(({ item }) => item.id === id);
    if (index === -1) return;
    analytics.track({
      event: 'select_item',
      params: {
        item_list_name: RECOMMENDATIONS_LIST_NAME,
        items: [{ item_name: recommendedEntries[index].module, index }],
      },
    });
    router.push({ pathname: id });
  };

  if (!open || !basketDetails) return null;

  return (
    <BasketDrawerTemplate
      notice={holdNotice}
      open={open}
      onClose={onClose}
      items={items}
      totalPrice={
        hasOnlyNoValueTableBookings(basketDetails.items)
          ? undefined
          : {
              amount: basketDetails.total.amount,
              currencyCode: basketDetails.total.currencyCode,
            }
      }
      itemCount={calculateTotalNumberOfItemsInBasket(basketDetails.items)}
      locale={language}
      view={view}
      onViewChange={setView}
      guests={guests}
      onRemoveItem={removeItem}
      removingItemId={removingItemId}
      isUpdating={isUpdating}
      onRemoveExtra={canRemoveExtras ? removeExtra : undefined}
      removingExtra={removingExtra}
      emptyMessage={t('basket.emptyBasketMessage')}
      recommendations={recommendations}
      onRecommendationSelect={selectRecommendation}
      onRecommendationsVisible={reportRecommendationsSeen}
      onViewBasket={() => router.push({ pathname: Routes.BASKET })}
      onCheckout={startCheckout}
      isCheckoutBlocked={Boolean(unacknowledgedError)}
      onContinueShopping={() => {
        onClose();
        router.push({ pathname: Routes.HOME });
      }}
      labels={{
        title: t('basketDrawer.title'),
        itemisedView: t('basket.itemisedView'),
        itineraryView: t('basket.itineraryView'),
        total: t('basketDrawer.total'),
        viewBasket: t('basketDrawer.viewBasket'),
        checkout: t('basketDrawer.checkout'),
        continueShopping: t('basket.continueShopping'),
        removeLabel: t('common.remove'),
        removeExtraLabel: t('common.remove'),
        removing: t('basket.removingItem'),
        recommendationsHeading: t('basketDrawer.recommendations.heading'),
        recommendationsViewItem: (name: string) =>
          t('basketDrawer.recommendations.viewItem', { name }),
      }}
    />
  );
};
